import { useCallback, useRef, useState } from 'react'

import { useDevicesApi } from '../api/context'
import { normalizeMac } from '../utils/mac'

interface UseMacValidationOptions {
  pbxId: number
  /** Excluded from uniqueness check (for edit mode) */
  deviceId?: number
  messages: {
    invalid: string
    alreadyInUse: string
  }
}

interface UseMacValidationResult {
  macError: string | null
  macValidating: boolean
  setMacError: (error: string | null) => void
  validateMac: (mac: string) => Promise<boolean>
  clearMacError: () => void
}

const COMPLETE_MAC_REGEX = /^[0-9a-f]{12}$/i

export function useMacValidation({
  pbxId,
  deviceId,
  messages,
}: UseMacValidationOptions): UseMacValidationResult {
  const api = useDevicesApi()
  const [macError, setMacError] = useState<string | null>(null)
  const [macValidating, setMacValidating] = useState(false)
  const validationRequestRef = useRef(0)

  const clearMacError = useCallback(() => {
    validationRequestRef.current += 1
    setMacError(null)
    setMacValidating(false)
  }, [])

  const validateMac = useCallback(
    async (mac: string): Promise<boolean> => {
      const requestId = validationRequestRef.current + 1
      validationRequestRef.current = requestId
      const trimmedMac = normalizeMac(mac)
      const applyLatestState = (update: () => void) => {
        if (validationRequestRef.current === requestId) {
          update()
        }
      }

      if (!trimmedMac) {
        applyLatestState(() => {
          setMacError(null)
          setMacValidating(false)
        })
        return true
      }

      const isCompleteMac = COMPLETE_MAC_REGEX.test(trimmedMac)

      if (!isCompleteMac) {
        applyLatestState(() => {
          setMacValidating(false)
          setMacError(messages.invalid)
        })
        return false
      }

      applyLatestState(() => {
        setMacValidating(true)
        setMacError(null)
      })

      try {
        const response = await api.validateMac(pbxId, trimmedMac, deviceId)

        if (response.ok) {
          const data = response.data as
            { error?: boolean; message?: string; valid?: boolean } | undefined
          if (data?.valid === true) {
            return true
          }
          if (data?.error === false) {
            return true
          }
          if (data?.error) {
            applyLatestState(() => {
              setMacError(data.message ?? messages.alreadyInUse)
            })
            return false
          }
          return true
        }

        const errorData = response.data as
          { error?: string; message?: string } | undefined
        const serverMessage =
          errorData?.error || errorData?.message || messages.invalid
        applyLatestState(() => {
          setMacError(serverMessage as string)
        })
        return false
      } catch {
        return true
      } finally {
        applyLatestState(() => {
          setMacValidating(false)
        })
      }
    },
    [api, pbxId, deviceId, messages.alreadyInUse, messages.invalid],
  )

  return {
    macError,
    macValidating,
    setMacError,
    validateMac,
    clearMacError,
  }
}
